<template>
  <v-card elevation="0" color="transparent" dark class="folder-panel">
    <v-card-title class="folder-panel-header">
      <div class="folder-panel-title">
        <div class="folder-eyebrow">Verzeichnisse</div>
        <div class="folder-heading">Ordner</div>
      </div>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="add-folder-button" aria-label="Neuen Ordner erstellen" v-bind="attrs" v-on="on" @click="$emit('create-folder')">
            <v-icon>mdi-folder-plus-outline</v-icon>
          </v-btn>
        </template>
        <span>Neuen Ordner erstellen</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text class="folder-panel-body">
      <v-treeview
        class="modern-folder-tree"
        activatable
        dense
        hoverable
        open-on-click
        transition
        :items="items"
        item-key="_id"
        item-text="name"
        item-children="children"
        :open.sync="localOpenNodes"
        @update:active="onActive">
        <template v-slot:label="{ item }">
          <div
            class="tree-label"
            @dragenter="handleDragEnter(item)"
            @dragleave="handleDragLeave(item)"
            @dragover.prevent
            @drop="onDrop($event, item)">
            <div class="tree-name">
              <v-icon small :color="folderIconColor(item)">{{ folderIcon(item) }}</v-icon>
              <span class="text-truncate">{{ item.name }}</span>
            </div>
            <v-spacer></v-spacer>
            <template v-if="item._id !== 'inbox' && item._id !== 'signed' && item._id !== 'notSigned' && item._id !== 'declined'">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small class="folder-menu-button" v-bind="attrs" v-on="on" :aria-label="`Aktionen für ${item.name}`" @click.stop>
                    <v-icon small>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list dark dense class="folder-menu">
                  <v-list-item @click.stop="$emit('create-subfolder', item)">
                    <v-list-item-icon><v-icon small>mdi-folder-plus-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Unterordner erstellen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click.stop="$emit('edit-folder', item)">
                    <v-list-item-icon><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Umbenennen</v-list-item-title>
                  </v-list-item>
                  <v-list-item class="delete-menu-item" @click.stop="$emit('delete-folder', item)">
                    <v-list-item-icon><v-icon small color="red lighten-2">mdi-trash-can-outline</v-icon></v-list-item-icon>
                    <v-list-item-title class="red--text text--lighten-2">Löschen</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </div>
        </template>
      </v-treeview>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "FolderTree",
  props: {
    items: {
      type: Array,
      required: true,
    },
    openNodes: {
      type: Array,
      required: true,
    },
  },
  computed: {
    localOpenNodes: {
      get() {
        return this.openNodes
      },
      set(value) {
        this.$emit("update:openNodes", value)
      },
    },
  },
  methods: {
    folderIcon(item) {
      if (item._id === "inbox") return "mdi-inbox-arrow-down-outline"
      if (item._id === "notSigned") return "mdi-file-clock-outline"
      if (item._id === "signed") return "mdi-file-check-outline"
      if (item._id === "declined") return "mdi-file-remove-outline"
      return item.children && item.children.length ? "mdi-folder-multiple-outline" : "mdi-folder-outline"
    },
    folderIconColor(item) {
      if (item._id === "notSigned") return "#f0bd45"
      if (item._id === "signed") return "#50c878"
      if (item._id === "declined") return "#ef6b6b"
      if (item._id === "inbox") return "#b3b3b7"
      return "#929297"
    },
    onActive(active) {
      if (active && active.length > 0) {
        const folderId = active[0]
        this.$emit("select-folder", folderId)
      } else {
        this.$emit("select-folder", null)
      }
    },
    onDrop(e, item) {
      if (item._id === "signed" || item._id === "notSigned" || item._id === "declined") return

      const documentIdsStr = e.dataTransfer.getData("documentIds")
      if (documentIdsStr) {
        try {
          const ids = JSON.parse(documentIdsStr)
          if (Array.isArray(ids) && ids.length > 0) {
            this.$emit("folder-drop", { folder: item, documentIds: ids })
            return
          }
        } catch (err) {
          console.error("Failed to parse documentIds", err)
        }
      }

      const documentId = e.dataTransfer.getData("documentId")
      if (documentId) {
        this.$emit("folder-drop", { folder: item, documentId })
      }
    },
    handleDragEnter(item) {
      this.$emit("drag-enter", item)
    },
    handleDragLeave(item) {
      this.$emit("drag-leave", item)
    },
  },
}
</script>

<style scoped>
.folder-panel {
  position: relative;
  width: 100%;
}

.folder-panel-header {
  display: flex !important;
  width: 100%;
  min-height: auto;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 16px;
  padding: 4px 5px 12px !important;
}

.folder-panel-title {
  min-width: 0;
}

.folder-eyebrow {
  color: #77777c;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.15em;
  line-height: 1.2;
  text-transform: uppercase;
}

.folder-heading {
  margin-top: 3px;
  color: #e8e8ea;
  font-size: 15px;
  font-weight: 750;
  line-height: 1.25;
}

.add-folder-button {
  flex: 0 0 auto;
  width: 40px !important;
  height: 40px !important;
  margin-left: auto !important;
  border: 1px solid rgba(0, 150, 64, 0.38);
  border-radius: 13px;
  color: #dbf5e5 !important;
  background: rgba(0, 150, 64, 0.14);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 9px 22px rgba(0, 0, 0, 0.16);
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease, box-shadow 160ms ease;
}

.add-folder-button:hover,
.add-folder-button:focus-visible {
  border-color: rgba(0, 150, 64, 0.7);
  background: rgba(0, 150, 64, 0.24);
  box-shadow: 0 11px 25px rgba(0, 0, 0, 0.22);
  transform: translateY(-1px);
}

.folder-panel-body {
  padding: 0 !important;
}

.modern-folder-tree {
  color: #c8c8cc;
}

.modern-folder-tree ::v-deep .v-treeview-node__root {
  min-height: 42px;
  margin: 2px 0;
  padding-right: 5px;
  border: 1px solid transparent;
  border-radius: 12px;
  transition: border-color 160ms ease, background-color 160ms ease, transform 160ms ease;
}

.modern-folder-tree ::v-deep .v-treeview-node__root:hover {
  border-color: rgba(255, 255, 255, 0.09);
  background: rgba(255, 255, 255, 0.045);
}

.modern-folder-tree ::v-deep .v-treeview-node__root.v-treeview-node--active {
  border-color: rgba(0, 150, 64, 0.32);
  color: #ffffff;
  background: linear-gradient(90deg, rgba(0, 150, 64, 0.2), rgba(0, 150, 64, 0.07));
  box-shadow: inset 3px 0 0 #009640;
}

.modern-folder-tree ::v-deep .v-treeview-node__toggle {
  color: #77777c;
  font-size: 18px;
}

.tree-label {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
}

.tree-name {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  font-weight: 600;
}

.folder-menu-button {
  opacity: 0;
  transition: opacity 150ms ease;
}

.tree-label:hover .folder-menu-button,
.folder-menu-button:focus {
  opacity: 1;
}

.folder-menu {
  min-width: 210px;
  padding: 7px !important;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 13px;
  background: rgba(28, 28, 29, 0.98) !important;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.42);
}

.folder-menu .v-list-item {
  min-height: 40px;
  border-radius: 9px;
}

.folder-menu .v-list-item__icon {
  margin: 10px 12px 10px 2px;
}

@media (hover: none) {
  .folder-menu-button {
    opacity: 1;
  }
}
</style>
